<template>
    <div>
        <div class="d-flex align-center mb-1">
            <h3 class="text-h6 font-weight-bold">Choose your plan</h3>
            <v-spacer/>
            <v-btn v-if="isPremiumActive" size="x-small" variant="text" color="error"
                   :loading="cancelling" @click="cancelPlan">
                Cancel plan
            </v-btn>
        </div>
        <p class="text-caption text-medium-emphasis mb-4">
            Unlock more location history and premium features for everyone in your circle.
        </p>

        <div v-if="loading" class="text-center py-8">
            <v-progress-circular color="primary" indeterminate size="28"/>
        </div>

        <v-row v-else dense>
            <v-col v-for="p in plans" :key="p.id" cols="12" sm="6" md="3">
                <v-card :variant="isCurrent(p) ? 'flat' : 'outlined'" rounded="xl"
                        :color="isCurrent(p) ? 'primary' : undefined"
                        :class="['h-100 d-flex flex-column plan-card', {'recommended': isRecommended(p)}]">
                    <div v-if="isRecommended(p) && !isCurrent(p)"
                         class="recommend-badge text-caption font-weight-bold">RECOMMENDED</div>

                    <v-card-item class="pb-1">
                        <div class="d-flex align-center">
                            <span class="text-subtitle-1 font-weight-bold">{{ p.name }}</span>
                            <v-chip v-if="isCurrent(p)" size="x-small" class="ms-2" color="white" variant="flat">
                                Current
                            </v-chip>
                        </div>
                        <div class="mt-1">
                            <span class="text-h5 font-weight-bold">{{ priceLabel(p) }}</span>
                            <span v-if="Number(p.price) > 0" class="text-caption text-medium-emphasis">
                                /{{ cycle(p.interval) }}
                            </span>
                        </div>
                    </v-card-item>

                    <v-card-text class="py-2 flex-grow-1">
                        <div class="d-flex align-baseline mb-2">
                            <v-icon size="18" :color="isCurrent(p) ? 'white' : 'primary'" class="me-1">
                                mdi-history
                            </v-icon>
                            <span class="text-body-2 font-weight-medium">{{ historyLabel(p) }} of history</span>
                        </div>
                        <div v-for="feat in featureLines(p)" :key="feat" class="d-flex align-center mb-1">
                            <v-icon size="15" :color="isCurrent(p) ? 'white' : 'success'" class="me-1">
                                mdi-check-circle
                            </v-icon>
                            <span class="text-caption">{{ feat }}</span>
                        </div>
                    </v-card-text>

                    <v-card-actions class="px-4 pb-4">
                        <v-btn v-if="isCurrent(p)" block variant="flat" color="white" disabled>
                            Your plan
                        </v-btn>
                        <v-btn v-else block :color="isRecommended(p) ? 'primary' : undefined"
                               :variant="isRecommended(p) ? 'flat' : 'tonal'"
                               :disabled="!purchasable(p)"
                               :loading="subscribingId === p.id" @click="choose(p)">
                            {{ !purchasable(p) ? 'Coming soon' : (isDowngrade(p) ? 'Downgrade' : (Number(p.price) > 0 ? 'Upgrade' : 'Choose')) }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>

        <!-- Single lightweight confirm, only when real money is involved. -->
        <v-dialog v-model="confirm.show" max-width="380">
            <v-card rounded="xl" class="pa-2">
                <v-card-title class="text-h6">{{ confirm.downgrade ? 'Confirm plan change' : 'Confirm upgrade' }}</v-card-title>
                <v-card-text v-if="confirm.downgrade">
                    Your <strong>{{ currentPlanName || 'current' }}</strong> plan keeps running until the end
                    of this cycle, then switches to <strong>{{ confirm.plan?.name }}</strong>. You won't be
                    charged today.
                </v-card-text>
                <v-card-text v-else>
                    Subscribe to <strong>{{ confirm.plan?.name }}</strong> for
                    {{ confirm.plan ? priceLabel(confirm.plan) : '' }}/{{ confirm.plan ? cycle(confirm.plan.interval) : '' }}?
                    Your card on file will be charged now.
                </v-card-text>
                <v-card-actions class="justify-end px-4 pb-3">
                    <v-btn variant="text" @click="confirm.show = false">Not now</v-btn>
                    <v-btn color="primary" variant="flat" :loading="!!subscribingId"
                           @click="subscribe(confirm.plan)">Confirm</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script setup>
import {computed, onMounted, reactive, ref} from "vue";
import {useRouter} from "vue-router";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";

const emits = defineEmits(['changed']);
const router = useRouter();
const {showSnackbar} = useSnackbarStore();

const loading = ref(true);
const plans = ref([]);
const entitlements = ref(null);
const subscribingId = ref(null);
const cancelling = ref(false);
const confirm = reactive({show: false, plan: null, downgrade: false});

const currentPlanName = computed(() => entitlements.value?.plan_name ?? null);
const isPremiumActive = computed(() => !!entitlements.value?.is_premium);
// Recommend an UPGRADE the circle can actually buy (drives upgrades). Only a
// premium plan that is a higher tier than the current one and is purchasable
// (a $0 "coming soon" premium plan is not) - never badge a downgrade or a
// disabled card. Empty (no badge) when the circle is already on the top tier.
const recommendedId = computed(() => {
    const upgrades = plans.value.filter(
        (p) => p.is_premium && purchasable(p) && !isCurrent(p) && Number(p.tier_rank ?? 0) > currentTier.value,
    );
    if (!upgrades.length) return null;
    // Prefer the plan named "Plus", else the highest tier_rank premium plan.
    const plus = upgrades.find((p) => (p.name || '').toLowerCase() === 'plus');
    return (plus ?? upgrades.sort((a, b) => (b.tier_rank ?? 0) - (a.tier_rank ?? 0))[0]).id;
});

// Current = the named plan the circle is on; or, when there's no active
// subscription (plan_name null), the free (non-premium) tier they sit on.
const isCurrent = (p) => currentPlanName.value
    ? p.name === currentPlanName.value
    : !p.is_premium;
const isRecommended = (p) => p.id === recommendedId.value;

// Tier of the circle's current plan (Free = 0 when there's no active premium).
// A lower-tier plan is a DOWNGRADE: it doesn't charge now and takes effect next
// cycle; a higher-tier paid plan is an UPGRADE and charges immediately.
const currentTier = computed(() => Number(entitlements.value?.tier_rank ?? 0));
const isDowngrade = (p) => !isCurrent(p) && Number(p.tier_rank ?? 0) < currentTier.value;
// A premium plan with no price set yet is not purchasable.
const purchasable = (p) => !(p.is_premium && !Number(p.price ?? 0));

const featureValue = (p, key) => {
    const f = (p.plan_features ?? []).find((x) => x.feature_key === key);
    return f ? f.value : null;
};
const historyLabel = (p) => {
    const days = Number(featureValue(p, 'history_days') ?? 7);
    if (days >= 30 && days % 30 === 0) {
        const m = days / 30;
        return `${m} month${m > 1 ? 's' : ''}`;
    }
    return `${days} day${days > 1 ? 's' : ''}`;
};
const featureLines = (p) => {
    const lines = [];
    if (p.is_premium) {
        lines.push('Places & Safe Zones', 'Speed & no-show alerts', 'Share live location');
    } else {
        lines.push('Basic live tracking', 'Family map');
    }
    return lines;
};
const priceLabel = (p) => {
    const price = Number(p.price ?? 0);
    // A premium plan with no price yet is not "Free" - it isn't purchasable.
    if (!price && p.is_premium) return 'Coming soon';
    if (!price) return 'Free';
    return new Intl.NumberFormat(undefined, {style: 'currency', currency: p.currency || 'USD'}).format(price);
};
const cycle = (interval) => ({0: 'mo', 1: 'qtr', 2: 'yr'}[interval] ?? 'mo');

const load = () => {
    loading.value = true;
    send('get', 'go-subscription')
        .then((r) => {
            plans.value = r.data?.data?.plans ?? [];
            entitlements.value = r.data?.data?.entitlements ?? null;
        })
        .catch(() => showSnackbar('Could not load plans', 'error'))
        .finally(() => loading.value = false);
};

// One tap: paid plans get a single confirm; free/downgrade go straight through.
const choose = (p) => {
    if (!purchasable(p)) return; // priced $0 premium = not available yet
    // A downgrade never charges now - confirm it takes effect next cycle.
    if (isDowngrade(p)) {
        confirm.plan = p;
        confirm.downgrade = true;
        confirm.show = true;
        return;
    }
    // A paid upgrade charges now - confirm.
    if (Number(p.price) > 0) {
        confirm.plan = p;
        confirm.downgrade = false;
        confirm.show = true;
        return;
    }
    // Free / no charge - go straight through.
    subscribe(p);
};

const subscribe = (p) => {
    if (!p) return;
    subscribingId.value = p.id;
    send('post', 'go-subscription/subscribe', {subscription_plan_id: p.id})
        .then((r) => {
            const data = r.data?.data ?? {};
            entitlements.value = data ?? entitlements.value;
            confirm.show = false;
            confirm.downgrade = false;
            // A downgrade is scheduled (current plan runs to cycle end); an
            // upgrade/new switches now. Message accordingly.
            showSnackbar(data?.plan_change?.scheduled
                ? (r.data?.message ?? `${p.name} starts at the end of your current cycle`)
                : `You're on ${p.name}`);
            emits('changed');
            load();
        })
        .catch((e) => {
            // 303 = a card is needed first; take them straight to add one.
            if (e.response?.status === 303 || e.response?.data?.data?.payment_required) {
                confirm.show = false;
                showSnackbar('Add a payment method to upgrade', 'info');
                router.push({name: 'payment-methods'});
                return;
            }
            showSnackbar(e.response?.data?.message ?? 'Could not change plan', 'error');
        })
        .finally(() => subscribingId.value = null);
};

const cancelPlan = () => {
    cancelling.value = true;
    send('post', 'go-subscription/cancel')
        .then((r) => {
            entitlements.value = r.data?.data ?? entitlements.value;
            showSnackbar('Plan cancelled - you keep access until it ends');
            emits('changed');
            load();
        })
        .catch(() => showSnackbar('Could not cancel', 'error'))
        .finally(() => cancelling.value = false);
};

onMounted(load);
</script>

<style scoped>
.plan-card {
    position: relative;
    transition: transform .12s ease, box-shadow .12s ease;
}
.plan-card.recommended {
    border-color: rgb(var(--v-theme-primary));
    box-shadow: 0 6px 22px rgba(var(--v-theme-primary), .18);
}
.plan-card:hover {
    transform: translateY(-3px);
}
.recommend-badge {
    position: absolute;
    top: 10px;
    right: -28px;
    transform: rotate(45deg);
    background: rgb(var(--v-theme-primary));
    color: #fff;
    padding: 2px 32px;
    letter-spacing: .5px;
}
</style>
